// src/lib/mdxQuery.js

export const MDX_QUERY_ENDPOINT = "/mdx/query/v2";

const PATH_SEP = "|||";

/**
 * Key identifying one leaf column (column group path + measure).
 * Matches the `path` / `measure` pair used when building leaf column defs,
 * including the single-column-dimension case where the group path is [""].
 */
export const mdxPercentColumnKey = (colPath, measure) =>
  `${colPath.join(PATH_SEP)}${PATH_SEP}${measure}`;

/**
 * Normalizes a raw cell value. Missing / blank values stay null (never 0).
 */
const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "-" || trimmed.toLowerCase() === "null") return null;
    const num = parseFloat(trimmed.replace(/,/g, ""));
    return isNaN(num) ? value : num;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  return null;
};

const getByPath = (obj, path) =>
  path.reduce(
    (acc, key) => (acc !== null && typeof acc === "object" ? acc[key] : undefined),
    obj
  );

/**
 * Walks the v2 `data` tree depth-first, calling `visit(node, rowPath, isPercent, hierarchy)`
 * for every node that carries `values`.
 * - `rows[]`     → members of the NEXT row dimension (row path grows by one level)
 * - `children[]` → hierarchy children within the SAME row dimension (`parent` = their
 *                  consolidated parent), shown right after the parent at the same level
 * Percent rows inherit their flag down `rows[]` (outer member applies to inner members);
 * hierarchy children carry their own flag.
 * `hierarchy` = { level, ancestorKeys, hasChildren } within the node's own dimension,
 * used for indenting and expand/collapse of children.
 */
const walkMdxV2Rows = (nodes, outerPath, inheritedPercent, visit, level = 0, ancestorKeys = []) => {
  if (!Array.isArray(nodes)) return;
  nodes.forEach((node) => {
    if (!node || typeof node !== "object") return;
    const rowPath = [...outerPath, String(node.name ?? "")];
    const rowKey = rowPath.join(PATH_SEP);
    const isPercent = inheritedPercent || node.isPercent === true;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;

    if (node.values && typeof node.values === "object") {
      visit(node, rowPath, isPercent, { level, ancestorKeys, hasChildren });
    }
    walkMdxV2Rows(node.rows, rowPath, isPercent, visit);
    walkMdxV2Rows(node.children, outerPath, inheritedPercent, visit, level + 1, [
      ...ancestorKeys,
      rowKey,
    ]);
  });
};

/**
 * Flattens a `/mdx/query/v2` response into the row structure the pivot grids consume.
 *
 * - `columns[]` is the source of truth for column order: every path segment except the
 *   last is the column group (e.g. version / month), the last segment is the measure.
 * - `data[]` is the source of truth for rows (see walkMdxV2Rows); cell values come from
 *   each row's `values` object, looked up by the column path.
 * - Percent flags: `columns[].isPercent` per leaf column, `isPercent` per row.
 */
export const flattenMdxV2Data = (response) => {
  const columns = Array.isArray(response?.columns) ? response.columns : [];
  const data = Array.isArray(response?.data) ? response.data : [];

  const columnPathKeys = [];
  const columnPathSeen = new Set();
  const measures = [];
  const measureSeen = new Set();
  const percentColumnsSet = new Set();

  columns.forEach((column) => {
    const path = Array.isArray(column?.path) ? column.path.map(String) : [];
    if (path.length === 0) return;

    const colPath = path.slice(0, -1);
    const measure = path[path.length - 1];
    const colPathKey = colPath.join(PATH_SEP);

    if (!columnPathSeen.has(colPathKey)) {
      columnPathSeen.add(colPathKey);
      columnPathKeys.push(colPathKey);
    }
    if (!measureSeen.has(measure)) {
      measureSeen.add(measure);
      measures.push(measure);
    }
    if (column.isPercent === true) {
      percentColumnsSet.add(mdxPercentColumnKey(colPath, measure));
    }
  });

  const flattenedRows = [];
  const rowsByKey = new Map();
  const percentRowsSet = new Set();

  walkMdxV2Rows(data, [], false, (dataRow, rowPath, isPercentRow, hierarchy) => {
    const rowKey = rowPath.join(PATH_SEP);
    if (isPercentRow) percentRowsSet.add(rowKey);

    let row = rowsByKey.get(rowKey);
    if (!row) {
      row = {
        _rowPath: [...rowPath],
        _columns: {},
        _isPercentRow: isPercentRow,
        _hierarchy: { key: rowKey, ...hierarchy },
      };
      rowPath.forEach((value, index) => {
        row[`RowLevel_${index + 1}`] = value;
      });
      rowsByKey.set(rowKey, row);
      flattenedRows.push(row);
    }

    // Every row gets every column/measure cell (null when absent) in `columns` order,
    // so measure order and column order stay identical across rows
    columnPathKeys.forEach((colPathKey) => {
      const colPath = colPathKey === "" ? [] : colPathKey.split(PATH_SEP);
      if (!row._columns[colPathKey]) row._columns[colPathKey] = {};
      const cells = row._columns[colPathKey];
      measures.forEach((measure) => {
        const value = normalizeCellValue(getByPath(dataRow.values, [...colPath, measure]));
        if (cells[measure] === undefined || cells[measure] === null) cells[measure] = value;
      });
    });
  });

  return {
    rows: flattenedRows,
    columnStructure: columnPathKeys.map((key) => key.split(PATH_SEP)),
    percentRows: Array.from(percentRowsSet),
    percentColumns: Array.from(percentColumnsSet),
  };
};

/**
 * True when the response has the v2 shape (`columns` + `data` arrays).
 */
export const isMdxV2Response = (response) =>
  Array.isArray(response?.data) && Array.isArray(response?.columns);
